import { useQuery, useQueryClient } from "@tanstack/react-query";
import { doc, getDoc } from "firebase/firestore";
import { firestore, isFirebaseConfigured } from "@/integrations/firebase/client";
import { DEFAULTS, type ContentId } from "@/lib/content-defaults";
import snapshotJson from "@/lib/content-snapshot.json";

// Copia del contenido vivo tomada al compilar (scripts/sincronizar-contenido.mjs). Es el valor
// inicial: el primer pintado y el prerender muestran lo real aunque Firestore tarde o falle.
const SNAPSHOT = snapshotJson as unknown as Partial<Record<ContentId, unknown>>;
function inicial(id: ContentId) {
  return (SNAPSHOT[id] ?? DEFAULTS[id]) as (typeof DEFAULTS)[ContentId];
}

const COLLECTION = "siteContent";

/**
 * Trae el contenido de una sección (ej. "home_hero") desde la colección
 * `siteContent` de Firestore. Si Firebase no está configurado, o la
 * sección todavía no fue guardada nunca, devuelve el valor por defecto —
 * el sitio jamás se rompe ni muestra vacíos.
 */
export function useContent<T = any>(id: ContentId): T {
  const { data } = useQuery({
    queryKey: ["site_content", id],
    queryFn: async () => {
      if (!isFirebaseConfigured) return inicial(id);
      const snap = await getDoc(doc(firestore, COLLECTION, id));
      if (!snap.exists()) return inicial(id);
      return snap.data().data;
    },
    initialData: inicial(id),
    initialDataUpdatedAt: 0, // forzar a que siempre intente traer el dato real al montar
    staleTime: 10_000,
  });
  return data as T;
}

/** Para usar dentro del panel /admin: invalida el cache tras guardar un cambio. */
export function useInvalidateContent() {
  const qc = useQueryClient();
  return (id: ContentId) => qc.invalidateQueries({ queryKey: ["site_content", id] });
}
